import { Button, Modal } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import SignatureCanvas from 'react-signature-canvas'
import '../../orgChart/orgChart.scss'

const SignatureModal = ({ open, onClose, onSave }) => {
  const sigPadRef = useRef(null)
  const [isEmpty, setIsEmpty] = useState(true)
  const isSavedRef = useRef(false) // 저장 여부 추적
  const isDrawingRef = useRef(false) // 그리기 중인지 추적
  const modalPositionRef = useRef({ x: 0, y: 0 }) // 모달 위치 추적

  // 캔버스 좌표 강제 재계산 (더블 모니터 대응)
  const forceCanvasUpdate = () => {
    if (!sigPadRef.current) return

    const canvas = sigPadRef.current.getCanvas()
    if (!canvas) return

    // 캔버스를 off/on하여 내부 좌표를 완전히 재계산
    sigPadRef.current.off()

    // requestAnimationFrame을 사용하여 다음 프레임에 재활성화
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (sigPadRef.current) {
          sigPadRef.current.on()
        }
      })
    })
  }

  // 모달 위치 변경 감지 및 캔버스 업데이트
  const checkModalPosition = () => {
    const modalElement = document.querySelector('.signature-modal .ant-modal')
    if (!modalElement) return

    const rect = modalElement.getBoundingClientRect()
    const currentPos = { x: rect.left, y: rect.top }

    // 위치가 변경되었으면 캔버스 재계산
    if (
      Math.abs(currentPos.x - modalPositionRef.current.x) > 1 ||
      Math.abs(currentPos.y - modalPositionRef.current.y) > 1
    ) {
      modalPositionRef.current = currentPos

      // 그리기 중이 아니면 즉시 업데이트
      if (!isDrawingRef.current) {
        forceCanvasUpdate()
      }
    }
  }

  // 모달이 열릴 때 캔버스 초기화 및 리사이즈
  useEffect(() => {
    if (open) {
      // 모달이 열릴 때마다 저장 상태 초기화
      isSavedRef.current = false

      // 캔버스 초기화 및 리사이즈 함수
      const initCanvas = () => {
        if (sigPadRef.current) {
          const canvas = sigPadRef.current.getCanvas()
          if (canvas) {
            // 캔버스 크기 설정
            const wrapper = canvas.parentElement
            if (wrapper) {
              const rect = wrapper.getBoundingClientRect()
              canvas.width = rect.width
              canvas.height = rect.height

              // 모달 위치 저장
              const modalElement = document.querySelector(
                '.signature-modal .ant-modal',
              )
              if (modalElement) {
                const modalRect = modalElement.getBoundingClientRect()
                modalPositionRef.current = {
                  x: modalRect.left,
                  y: modalRect.top,
                }
              }
            }
          }
          // 좌표 재계산을 위해 off/on
          sigPadRef.current.off()
          requestAnimationFrame(() => {
            if (sigPadRef.current) {
              sigPadRef.current.on()
              sigPadRef.current.clear()
              setIsEmpty(true)
            }
          })
        }
      }

      // 첫 번째 마우스 이벤트를 위한 좌표 사전 계산
      const setupFirstDrawFix = () => {
        if (sigPadRef.current) {
          const canvas = sigPadRef.current.getCanvas()
          if (canvas) {
            // 마우스 다운 이벤트를 캐치하여 첫 번째 그리기 전에 좌표 재계산
            const handleFirstMouseDown = () => {
              // 좌표를 강제로 재계산
              const rect = canvas.getBoundingClientRect()
              void rect.width
              void rect.height

              // react-signature-canvas의 내부 좌표 갱신을 위해
              // 라이브러리가 사용하는 좌표 계산을 트리거
              if (canvas.parentElement) {
                const wrapperRect = canvas.parentElement.getBoundingClientRect()
                void wrapperRect.width
                void wrapperRect.height
              }
            }

            // 마우스와 터치 이벤트 모두 처리 (once: true로 자동 제거)
            canvas.addEventListener('mousedown', handleFirstMouseDown, {
              once: true,
            })
            canvas.addEventListener('touchstart', handleFirstMouseDown, {
              once: true,
            })
          }
        }
      }

      // 즉시 캔버스 초기화 시도
      initCanvas()
      setupFirstDrawFix()

      // 모달이 완전히 렌더링된 후 캔버스를 재초기화
      const timer = setTimeout(() => {
        initCanvas()
        setupFirstDrawFix()
      }, 100)

      // 추가 재시도 (ref가 늦게 설정될 수 있음)
      const retryTimer = setTimeout(() => {
        initCanvas()
        setupFirstDrawFix()
      }, 200)

      return () => {
        clearTimeout(timer)
        clearTimeout(retryTimer)
      }
    } else {
      // 모달이 닫힐 때 저장하지 않았다면 캔버스 초기화
      if (!isSavedRef.current && sigPadRef.current) {
        sigPadRef.current.clear()
        setIsEmpty(true)
      }
    }
  }, [open])

  // 모달 이동/크기 변경 감지 및 리사이즈 이벤트 처리 (더블 모니터 대응)
  useEffect(() => {
    if (!open) return

    const modalElement = document.querySelector('.signature-modal .ant-modal')
    let observer = null
    let positionCheckInterval = null

    // 리사이즈 이벤트 핸들러
    const handleResize = () => {
      if (isDrawingRef.current) return

      setTimeout(() => {
        if (sigPadRef.current && !isDrawingRef.current) {
          forceCanvasUpdate()
        }
      }, 100)
    }

    // 모달 위치 주기적 체크 (더블 모니터 이동 감지)
    const startPositionCheck = () => {
      // 초기 위치 저장
      if (modalElement) {
        const rect = modalElement.getBoundingClientRect()
        modalPositionRef.current = { x: rect.left, y: rect.top }
      }

      // 100ms마다 위치 체크 (더블 모니터 이동 감지)
      positionCheckInterval = setInterval(() => {
        if (!isDrawingRef.current) {
          checkModalPosition()
        }
      }, 100)
    }

    if (modalElement) {
      // MutationObserver로 스타일 변경 감지
      observer = new MutationObserver(() => {
        if (isDrawingRef.current) return

        // 위치 체크
        checkModalPosition()
      })

      observer.observe(modalElement, {
        attributes: true,
        attributeFilter: ['style', 'class'],
      })

      // 위치 추적 시작
      startPositionCheck()
    }

    // 윈도우 이벤트 리스너
    window.addEventListener('resize', handleResize)
    window.addEventListener('orientationchange', handleResize)
    // 더블 모니터 이동 감지를 위한 추가 이벤트
    window.addEventListener('move', handleResize)

    return () => {
      if (observer) {
        observer.disconnect()
      }
      if (positionCheckInterval) {
        clearInterval(positionCheckInterval)
      }
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('orientationchange', handleResize)
      window.removeEventListener('move', handleResize)
    }
  }, [open])

  const handleClear = () => {
    sigPadRef.current?.clear()
    setIsEmpty(true)
  }

  const handleBegin = () => {
    // 그리기 시작 전에 좌표 강제 재계산
    if (sigPadRef.current) {
      const canvas = sigPadRef.current.getCanvas()
      if (canvas) {
        // 캔버스 위치 강제 갱신 (좌표 계산 트리거)
        const rect = canvas.getBoundingClientRect()
        void rect.width
        void rect.height
      }
    }

    isDrawingRef.current = true
    setIsEmpty(false)
  }

  const handleEnd = () => {
    isDrawingRef.current = false
    setIsEmpty(sigPadRef.current?.isEmpty() ?? true)
  }

  const handleSave = () => {
    if (sigPadRef.current && !sigPadRef.current.isEmpty()) {
      const dataURL = sigPadRef.current.toDataURL('image/png')
      isSavedRef.current = true // 저장 완료 표시
      onSave?.(dataURL)
      handleClear()
      onClose()
    }
  }

  const handleClose = () => {
    // 모달을 닫을 때 저장하지 않았다면 캔버스 초기화
    if (!isSavedRef.current && sigPadRef.current) {
      sigPadRef.current.clear()
      setIsEmpty(true)
    }
    onClose()
  }

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      title="서명 등록하기"
      className="signature-modal"
      width={600}
      centered
      afterOpenChange={(visible) => {
        // 모달이 완전히 열린 후 캔버스 초기화 및 리사이즈
        if (visible) {
          // 저장 상태 초기화
          isSavedRef.current = false

          const initCanvas = () => {
            if (sigPadRef.current) {
              const canvas = sigPadRef.current.getCanvas()
              if (canvas) {
                // 캔버스 크기 설정
                const wrapper = canvas.parentElement
                if (wrapper) {
                  const rect = wrapper.getBoundingClientRect()
                  canvas.width = rect.width
                  canvas.height = rect.height

                  // 모달 위치 저장
                  const modalElement = document.querySelector(
                    '.signature-modal .ant-modal',
                  )
                  if (modalElement) {
                    const modalRect = modalElement.getBoundingClientRect()
                    modalPositionRef.current = {
                      x: modalRect.left,
                      y: modalRect.top,
                    }
                  }
                }
              }
              // 좌표 재계산을 위해 off/on
              sigPadRef.current.off()
              requestAnimationFrame(() => {
                if (sigPadRef.current) {
                  sigPadRef.current.on()
                  sigPadRef.current.clear()
                  setIsEmpty(true)
                }
              })
            }
          }

          // 즉시 초기화
          initCanvas()

          setTimeout(() => {
            initCanvas()
          }, 50)
        }
      }}
    >
      <div className="signature-canvas-container">
        <div className="signature-canvas-wrapper">
          <SignatureCanvas
            ref={sigPadRef}
            canvasProps={{
              className: 'signature-canvas',
            }}
            onBegin={handleBegin}
            onEnd={handleEnd}
            penColor="#000000"
            backgroundColor="rgba(255,255,255,0)"
          />
        </div>
        <div className="signature-actions">
          <Button onClick={handleClear} className="btn-clear">
            다시 그리기
          </Button>
          <Button
            type="primary"
            onClick={handleSave}
            disabled={isEmpty}
            className="btn-save"
          >
            서명 등록하기
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default SignatureModal
